import { useEffect, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { MainNavbar } from '../../components/navigation/MainNavbar';
import { usePublicAuth } from '../../contexts/PublicAuthContext.jsx';
import {
  collection,
  query,
  where,
  getDocs,
  doc,
  getDoc,
  updateDoc,
  onSnapshot,
} from 'firebase/firestore';
import { db } from '../../config/firebase';
import {
  MessageCircle,
  CalendarDays,
  MapPin,
  Box,
  CheckCircle,
  XCircle,
  Clock,
  Package,
  User,
  DollarSign,
  Plane,
  Truck,
  Car,
  Star,
  MessageSquare,
} from 'lucide-react';
import airportImage from '../../assets/airportt.png';
import ReviewModal from '../../components/reviews/ReviewModal';
import { hasExistingReview } from '../../services/reviewsService';
import { useAppDispatch } from '../../store/hooks';
import { openOrCreateConversationByUserId } from '../../store/chatThunks';

const PAGE_SIZE = 12;

const formatDate = date => {
  if (!date) return 'N/A';
  let dateObj;
  if (typeof date === 'number') {
    dateObj = new Date(date);
  } else if (date && typeof date.toDate === 'function') {
    dateObj = date.toDate();
  } else if (date instanceof Date) {
    dateObj = date;
  } else if (typeof date === 'string') {
    dateObj = new Date(date);
    if (isNaN(dateObj.getTime())) {
      return 'N/A';
    }
  } else {
    return 'N/A';
  }

  if (isNaN(dateObj.getTime())) {
    return 'N/A';
  }

  return dateObj.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
};

const formatTime = time => {
  if (!time) return 'N/A';
  if (typeof time === 'string') {
    return time;
  }
  if (time instanceof Date) {
    return time.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    });
  }
  return 'N/A';
};

const getStatusIcon = status => {
  switch (status) {
    case 'pending':
      return <Clock className="h-4 w-4 text-yellow-500" />;
    case 'accepted':
      return <CheckCircle className="h-4 w-4 text-blue-500" />;
    case 'rejected':
      return <XCircle className="h-4 w-4 text-red-500" />;
    case 'completed':
      return <CheckCircle className="h-4 w-4 text-green-500" />;
    case 'delivered':
      return <Package className="h-4 w-4 text-purple-500" />;
    case 'cancelled':
      return <XCircle className="h-4 w-4 text-gray-500" />;
    default:
      return <Clock className="h-4 w-4 text-gray-500" />;
  }
};

const getStatusColor = status => {
  switch (status) {
    case 'pending':
      return 'bg-yellow-50 border-yellow-200 text-yellow-800';
    case 'accepted':
      return 'bg-blue-50 border-blue-200 text-blue-800';
    case 'rejected':
      return 'bg-red-50 border-red-200 text-red-800';
    case 'completed':
      return 'bg-green-50 border-green-200 text-green-800';
    case 'delivered':
      return 'bg-purple-50 border-purple-200 text-purple-800';
    case 'cancelled':
      return 'bg-gray-50 border-gray-200 text-gray-800';
    default:
      return 'bg-gray-50 border-gray-200 text-gray-800';
  }
};

const getStatusLabel = (status, t) => {
  switch (status) {
    case 'pending':
      return t('common.pending');
    case 'accepted':
      return t('common.accepted');
    case 'rejected':
      return t('common.rejected');
    case 'completed':
      return t('common.completed');
    case 'delivered':
      return t('common.delivered');
    case 'cancelled':
      return t('common.cancelled');
    default:
      return status?.toUpperCase() || t('common.unknown');
  }
};

const getDeliveryMethodIcon = method => {
  switch (method) {
    case 'byRoad':
      return <Car className="h-4 w-4 text-gray-500" />;
    case 'byRail':
      return <Truck className="h-4 w-4 text-gray-500" />;
    case 'byAir':
      return <Plane className="h-4 w-4 text-gray-500" />;
    default:
      return <Car className="h-4 w-4 text-gray-500" />;
  }
};

const getDeliveryMethodLabel = (method, t) => {
  switch (method) {
    case 'byRoad':
      return t('travelerTripRequests.byRoad');
    case 'byRail':
      return t('travelerTripRequests.byRail');
    case 'byAir':
      return t('travelerTripRequests.byAir');
    default:
      return t('travelerTripRequests.byRoad');
  }
};

export default function MyRequests() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user, initializing } = usePublicAuth();
  const dispatch = useAppDispatch();
  const [requests, setRequests] = useState([]);
  const [trips, setTrips] = useState({}); // { tripId: tripData }
  const [tripOwnerNames, setTripOwnerNames] = useState({}); // { tripId: ownerName }
  const [loading, setLoading] = useState(true);
  const [processingRequest, setProcessingRequest] = useState(null);
  const [reviewModal, setReviewModal] = useState({
    visible: false,
    reviewedUserId: null,
    reviewedUserName: '',
    requestId: null,
    role: 'sender',
  });
  const [reviewStatuses, setReviewStatuses] = useState({});
  const [currentPage, setCurrentPage] = useState(1);

  useEffect(() => {
    if (!user?.uid && !user?.email) {
      setLoading(false);
      return;
    }

    const loadData = async () => {
      try {
        // Load user's requests (as requester)
        const requestsQueries = [];

        // Query by requesterId
        if (user?.uid) {
          requestsQueries.push(
            query(
              collection(db, 'requests'),
              where('requesterId', '==', user.uid),
            ),
          );
        }

        // Query by requesterEmail
        if (user?.email) {
          requestsQueries.push(
            query(
              collection(db, 'requests'),
              where('requesterEmail', '==', user.email),
            ),
          );
        }

        if (requestsQueries.length === 0) {
          setLoading(false);
          return;
        }

        const allRequestsSnapshots = await Promise.all(
          requestsQueries.map(q => getDocs(q)),
        );

        const requestMap = new Map();
        allRequestsSnapshots.forEach(snapshot => {
          snapshot.docs.forEach(doc => {
            const data = doc.data();
            requestMap.set(doc.id, {
              id: doc.id,
              ...data,
            });
          });
        });
        const allRequests = Array.from(requestMap.values());

        // Filter only carrying trips (traveler requests)
        const carryingRequests = [];
        const tripsData = {};

        // Load trips data for each request
        if (allRequests.length > 0) {
          const requestPromises = allRequests.map(async request => {
            if (!request.tripId) return null;

            try {
              // Check if trip already loaded
              if (!tripsData[request.tripId]) {
                const tripDoc = await getDoc(doc(db, 'trips', request.tripId));
                if (tripDoc.exists()) {
                  const tripData = { id: tripDoc.id, ...tripDoc.data() };
                  tripsData[request.tripId] = tripData;
                } else {
                  return null;
                }
              }

              const trip = tripsData[request.tripId];
              // Only include requests for carrying trips
              if (trip && trip.tripType === 'carrying') {
                return request;
              }
            } catch (error) {
              console.error('Error loading trip:', error);
            }
            return null;
          });

          const requestResults = await Promise.all(requestPromises);
          requestResults.forEach(result => {
            if (result) {
              carryingRequests.push(result);
            }
          });
        }

        // Load trip owner names
        const ownerNames = {};
        const ownerPromises = Object.values(tripsData).map(async trip => {
          if (!trip.createdBy && !trip.ownerId) {
            ownerNames[trip.id] = 'Trip Owner';
            return;
          }
          const ownerId = trip.createdBy || trip.ownerId;
          if (ownerNames[trip.id]) return;

          try {
            const ownerDoc = await getDoc(doc(db, 'users', ownerId));
            if (ownerDoc.exists()) {
              const ownerData = ownerDoc.data();
              ownerNames[trip.id] =
                ownerData.displayName ||
                `${ownerData.firstName || ''} ${
                  ownerData.lastName || ''
                }`.trim() ||
                ownerData.email?.split('@')[0] ||
                'Trip Owner';
            } else {
              ownerNames[trip.id] = 'Trip Owner';
            }
          } catch (error) {
            console.error('Error loading owner name:', error);
            ownerNames[trip.id] = 'Trip Owner';
          }
        });

        await Promise.all(ownerPromises);

        setRequests(carryingRequests);
        setTrips(tripsData);
        setTripOwnerNames(ownerNames);
      } catch (error) {
        console.error('Error loading data:', error);
      } finally {
        setLoading(false);
      }
    };

    loadData();

    // Set up real-time listeners
    if (user?.uid || user?.email) {
      const unsubscribes = [];

      // Listen to requests by requesterId
      if (user?.uid) {
        const requestsQuery = query(
          collection(db, 'requests'),
          where('requesterId', '==', user.uid),
        );

        const unsubscribe = onSnapshot(requestsQuery, async snapshot => {
          const requestsData = snapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data(),
          }));

          // Filter carrying trips
          const carryingRequests = [];
          const tripsData = { ...trips };
          const ownerNames = { ...tripOwnerNames };

          // Process each request
          const requestPromises = requestsData.map(async request => {
            if (!request.tripId) return null;

            try {
              // Load trip if not already loaded
              if (!tripsData[request.tripId]) {
                const tripDoc = await getDoc(doc(db, 'trips', request.tripId));
                if (tripDoc.exists()) {
                  const tripData = { id: tripDoc.id, ...tripDoc.data() };
                  tripsData[request.tripId] = tripData;

                  // Load owner name if not already loaded
                  if (
                    !ownerNames[request.tripId] &&
                    (tripData.createdBy || tripData.ownerId)
                  ) {
                    const ownerId = tripData.createdBy || tripData.ownerId;
                    try {
                      const ownerDoc = await getDoc(doc(db, 'users', ownerId));
                      if (ownerDoc.exists()) {
                        const ownerData = ownerDoc.data();
                        ownerNames[request.tripId] =
                          ownerData.displayName ||
                          `${ownerData.firstName || ''} ${
                            ownerData.lastName || ''
                          }`.trim() ||
                          ownerData.email?.split('@')[0] ||
                          'Trip Owner';
                      } else {
                        ownerNames[request.tripId] = 'Trip Owner';
                      }
                    } catch (error) {
                      console.error('Error loading owner name:', error);
                      ownerNames[request.tripId] = 'Trip Owner';
                    }
                  } else if (!ownerNames[request.tripId]) {
                    ownerNames[request.tripId] = 'Trip Owner';
                  }
                } else {
                  return null;
                }
              }

              const trip = tripsData[request.tripId];
              // Only include requests for carrying trips
              if (trip && trip.tripType === 'carrying') {
                return request;
              }
            } catch (error) {
              console.error('Error loading trip:', error);
            }
            return null;
          });

          const requestResults = await Promise.all(requestPromises);
          requestResults.forEach(result => {
            if (result) {
              carryingRequests.push(result);
            }
          });

          setRequests(carryingRequests);
          setTrips(tripsData);
          setTripOwnerNames(ownerNames);
        });

        unsubscribes.push(unsubscribe);
      }

      // Listen to requests by requesterEmail
      if (user?.email) {
        const requestsQuery = query(
          collection(db, 'requests'),
          where('requesterEmail', '==', user.email),
        );

        const unsubscribe = onSnapshot(requestsQuery, async snapshot => {
          const requestsData = snapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data(),
          }));

          // Filter carrying trips
          const carryingRequests = [];
          const tripsData = { ...trips };
          const ownerNames = { ...tripOwnerNames };

          // Process each request
          const requestPromises = requestsData.map(async request => {
            if (!request.tripId) return null;

            try {
              // Load trip if not already loaded
              if (!tripsData[request.tripId]) {
                const tripDoc = await getDoc(doc(db, 'trips', request.tripId));
                if (tripDoc.exists()) {
                  const tripData = { id: tripDoc.id, ...tripDoc.data() };
                  tripsData[request.tripId] = tripData;

                  // Load owner name if not already loaded
                  if (
                    !ownerNames[request.tripId] &&
                    (tripData.createdBy || tripData.ownerId)
                  ) {
                    const ownerId = tripData.createdBy || tripData.ownerId;
                    try {
                      const ownerDoc = await getDoc(doc(db, 'users', ownerId));
                      if (ownerDoc.exists()) {
                        const ownerData = ownerDoc.data();
                        ownerNames[request.tripId] =
                          ownerData.displayName ||
                          `${ownerData.firstName || ''} ${
                            ownerData.lastName || ''
                          }`.trim() ||
                          ownerData.email?.split('@')[0] ||
                          'Trip Owner';
                      } else {
                        ownerNames[request.tripId] = 'Trip Owner';
                      }
                    } catch (error) {
                      console.error('Error loading owner name:', error);
                      ownerNames[request.tripId] = 'Trip Owner';
                    }
                  } else if (!ownerNames[request.tripId]) {
                    ownerNames[request.tripId] = 'Trip Owner';
                  }
                } else {
                  return null;
                }
              }

              const trip = tripsData[request.tripId];
              // Only include requests for carrying trips
              if (trip && trip.tripType === 'carrying') {
                return request;
              }
            } catch (error) {
              console.error('Error loading trip:', error);
            }
            return null;
          });

          const requestResults = await Promise.all(requestPromises);
          requestResults.forEach(result => {
            if (result) {
              carryingRequests.push(result);
            }
          });

          setRequests(prev => {
            // Merge with existing requests, avoiding duplicates and updating existing ones
            const requestMap = new Map(prev.map(r => [r.id, r]));
            carryingRequests.forEach(r => {
              requestMap.set(r.id, r);
            });
            return Array.from(requestMap.values());
          });
          setTrips(tripsData);
          setTripOwnerNames(ownerNames);
        });

        unsubscribes.push(unsubscribe);
      }

      return () => {
        unsubscribes.forEach(unsub => unsub());
      };
    }
  }, [user?.uid, user?.email]);

  // Pagination effect
  useEffect(() => {
    const sortedRequests = [...requests].sort((a, b) => {
      const aTime = a.createdAt?.toDate
        ? a.createdAt.toDate().getTime()
        : a.createdAt
        ? new Date(a.createdAt).getTime()
        : 0;
      const bTime = b.createdAt?.toDate
        ? b.createdAt.toDate().getTime()
        : b.createdAt
        ? new Date(b.createdAt).getTime()
        : 0;
      return bTime - aTime;
    });
    const totalPages = Math.max(
      1,
      Math.ceil(sortedRequests.length / PAGE_SIZE),
    );
    if (currentPage > totalPages) {
      setCurrentPage(Math.max(1, totalPages));
    }
  }, [requests, currentPage]);

  const handleCancelRequest = async requestId => {
    if (processingRequest) return;
    if (!confirm(t('travelerMyRequests.areYouSureCancel'))) {
      return;
    }
    setProcessingRequest(requestId);
    try {
      const requestRef = doc(db, 'requests', requestId);
      await updateDoc(requestRef, {
        status: 'cancelled',
        updatedAt: new Date(),
      });
      alert(t('travelerMyRequests.requestCancelledSuccessfully'));
    } catch (error) {
      console.error('Error cancelling request:', error);
      alert(t('travelerMyRequests.failedToCancelRequest'));
    } finally {
      setProcessingRequest(null);
    }
  };

  const getTripOwnerInfo = request => {
    const trip = trips[request.tripId];
    return {
      id:
        request.tripOwnerId || trip?.createdBy || trip?.ownerId || trip?.userId,
      name:
        tripOwnerNames[request.tripId] ||
        request.tripOwnerName ||
        trip?.ownerName ||
        trip?.createdByName ||
        trip?.createdByEmail?.split('@')[0] ||
        'Trip Owner',
    };
  };

  const handleOpenReviewModal = async request => {
    if (!user?.uid) {
      alert(t('travelerMyRequests.pleaseLogInToRate'));
      return;
    }

    const ownerInfo = getTripOwnerInfo(request);
    if (!ownerInfo.id) {
      alert(t('travelerMyRequests.tripOwnerInfoMissing'));
      return;
    }

    try {
      const alreadyReviewed = await hasExistingReview(
        ownerInfo.id,
        request.id,
        user.uid,
      );
      if (alreadyReviewed) {
        setReviewStatuses(prev => ({ ...prev, [request.id]: true }));
        alert(t('travelerMyRequests.alreadyReviewed'));
        return;
      }

      setReviewModal({
        visible: true,
        reviewedUserId: ownerInfo.id,
        reviewedUserName: ownerInfo.name,
        requestId: request.id,
        role: 'sender',
      });
    } catch (error) {
      alert(error.message || t('travelerMyRequests.failedToOpenReviewModal'));
    }
  };

  const handleReviewSuccess = requestId => {
    setReviewStatuses(prev => ({ ...prev, [requestId]: true }));
  };

  const handleStartChat = async (targetUserId, targetUserName) => {
    if (!user) {
      navigate('/auth/login');
      return;
    }

    if (!targetUserId) {
      navigate('/chat');
      return;
    }

    try {
      const conversation = await dispatch(
        openOrCreateConversationByUserId(
          targetUserId,
          targetUserName || 'Trip Owner',
        ),
      );
      if (conversation?.id) {
        navigate(`/chat/${conversation.id}`);
      } else {
        navigate('/chat');
      }
    } catch (error) {
      console.error('Error opening chat:', error);
      navigate('/chat');
    }
  };

  useEffect(() => {
    if (!user?.uid || requests.length === 0) {
      setReviewStatuses({});
      return;
    }

    let isMounted = true;

    const fetchStatuses = async () => {
      try {
        const entries = await Promise.all(
          requests.map(async request => {
            const ownerInfo = getTripOwnerInfo(request);
            if (!ownerInfo.id) {
              return [request.id, false];
            }
            try {
              const exists = await hasExistingReview(
                ownerInfo.id,
                request.id,
                user.uid,
              );
              return [request.id, exists];
            } catch (error) {
              console.error('Error checking review status:', error);
              return [request.id, false];
            }
          }),
        );

        if (!isMounted) return;
        const statusMap = {};
        entries.forEach(([id, status]) => {
          if (id) statusMap[id] = status;
        });
        setReviewStatuses(statusMap);
      } catch (error) {
        console.error('Error loading review statuses:', error);
      }
    };

    fetchStatuses();

    return () => {
      isMounted = false;
    };
  }, [requests, user?.uid, trips, tripOwnerNames]);

  const handleMarkAsDelivered = async requestId => {
    if (processingRequest) return;
    if (!confirm(t('travelerMyRequests.markAsDeliveredConfirm'))) {
      return;
    }
    setProcessingRequest(requestId);
    try {
      const requestRef = doc(db, 'requests', requestId);
      await updateDoc(requestRef, {
        status: 'delivered',
        updatedAt: new Date(),
      });
      alert(t('travelerMyRequests.itemMarkedAsDelivered'));
    } catch (error) {
      console.error('Error marking as delivered:', error);
      alert(t('travelerMyRequests.failedToMarkAsDelivered'));
    } finally {
      setProcessingRequest(null);
    }
  };

  const renderRequestCard = request => {
    const trip = trips[request.tripId];
    if (!trip) return null;
    const ownerInfo = getTripOwnerInfo(request);

    const canCancel =
      request.status === 'pending' || request.status === 'accepted';
    const canMarkAsDelivered = request.status === 'accepted';

    return (
      <div className="group relative rounded-2xl bg-white dark:bg-slate-900 p-6 shadow-md border border-gray-200 dark:border-slate-800 hover:shadow-xl hover:border-gray-300 dark:hover:border-slate-700 transition-all duration-300">
        {/* Header */}
        <div className="flex items-start justify-between mb-4 pb-4 border-b border-gray-100 dark:border-slate-700">
          <div className="flex items-center gap-3 flex-1 min-w-0">
            <div className="flex-shrink-0 w-12 h-12 rounded-full bg-black dark:bg-slate-700 flex items-center justify-center">
              <User className="h-6 w-6 text-white" />
            </div>
            <div className="flex-1 min-w-0">
              <h3 className="font-bold text-gray-900 dark:text-white truncate">
                {tripOwnerNames[request.tripId] ||
                  request.tripOwnerName ||
                  t('travelerMyRequests.tripOwner')}
              </h3>
              <p className="text-xs text-gray-600 dark:text-slate-400 truncate">
                {t('travelerMyRequests.tripOwner')}
              </p>
            </div>
          </div>
          <div
            className={`flex items-center gap-2 px-3 py-1.5 rounded-full border ${getStatusColor(
              request.status,
            )} flex-shrink-0`}
          >
            {getStatusIcon(request.status)}
            <span className="text-xs font-bold">
              {getStatusLabel(request.status, t)}
            </span>
          </div>
        </div>

        {/* Item Info */}
        <div className="mb-4">
          <div className="flex items-center gap-2 mb-2">
            <Package className="h-4 w-4 text-purple-500 dark:text-purple-400" />
            <p className="text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-slate-400">
              {t('travelerMyRequests.item')}
            </p>
          </div>
          <p className="text-base font-bold text-gray-900 dark:text-white">
            {request.itemTitle || request.itemDescription || 'N/A'}
          </p>
        </div>

        {/* Route Info */}
        <div className="mb-4 p-3 bg-gray-50 dark:bg-slate-800 rounded-lg">
          <div className="flex items-center gap-2 mb-2">
            <MapPin className="h-4 w-4 text-yellow-500 dark:text-yellow-400" />
            <p className="text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-slate-400">
              {t('travelerMyRequests.route')}
            </p>
          </div>
          <p className="text-sm font-bold text-gray-900 dark:text-white">
            {trip.originDetails?.city || trip.originCity || 'Origin'},{' '}
            {trip.originDetails?.country || trip.originCountry || ''} →{' '}
            {trip.destinationDetails?.city ||
              trip.destinationCity ||
              'Destination'}
            ,{' '}
            {trip.destinationDetails?.country || trip.destinationCountry || ''}
          </p>
        </div>

        {/* Trip Details */}
        <div className="mb-4 space-y-2">
          <div className="flex items-center gap-2">
            <CalendarDays className="h-4 w-4 text-blue-500 dark:text-blue-400" />
            <p className="text-xs text-gray-600 dark:text-slate-400">
              {t('travelerMyRequests.departure')}:{' '}
              {formatDate(trip.departureDate)} {t('common.at')}{' '}
              {formatTime(trip.departureTime)}
            </p>
          </div>

          {request.availableSpace && (
            <div className="flex items-center gap-2">
              <Box className="h-4 w-4 text-gray-500 dark:text-slate-400" />
              <p className="text-xs text-gray-600 dark:text-slate-400">
                {t('travelerMyRequests.space')}: {request.availableSpace}{' '}
                {request.spaceUnit || trip.spaceUnit || 'kg'}
              </p>
            </div>
          )}

          {trip.deliveryMethod && (
            <div className="flex items-center gap-2">
              {getDeliveryMethodIcon(trip.deliveryMethod)}
              <p className="text-xs text-gray-600 dark:text-slate-400">
                {t('travelerMyRequests.delivery')}:{' '}
                {getDeliveryMethodLabel(trip.deliveryMethod, t)}
              </p>
            </div>
          )}

          {(request.price || request.estimatedPrice) && (
            <div className="flex items-center gap-2">
              <DollarSign className="h-4 w-4 text-yellow-500 dark:text-yellow-400" />
              <p className="text-xs font-bold text-gray-900 dark:text-white">
                {t('travelerMyRequests.price')}: {request.currency || 'USD'}{' '}
                {request.price || request.estimatedPrice || 'N/A'}
              </p>
            </div>
          )}
        </div>

        {/* Notes */}
        {request.additionalNotes && (
          <div className="mb-4 p-3 bg-gray-50 dark:bg-slate-800 rounded-lg border border-gray-200 dark:border-slate-700">
            <p className="text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-slate-400 mb-1">
              {t('travelerMyRequests.notes')}
            </p>
            <p className="text-sm text-gray-700 dark:text-slate-300 line-clamp-2">
              {request.additionalNotes}
            </p>
          </div>
        )}

        {/* Waiting message for delivered status */}
        {request.status === 'delivered' && (
          <div className="mb-4 p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
            <div className="flex items-center gap-2">
              <Clock className="h-4 w-4 text-blue-600 dark:text-blue-400" />
              <p className="text-sm font-semibold text-blue-900 dark:text-blue-300">
                {t('travelerMyRequests.waitingForSenderConfirmation')}
              </p>
            </div>
          </div>
        )}

        {/* Actions */}
        <div className="flex flex-col gap-3 pt-4 border-t border-gray-100 dark:border-slate-700">
          {request.status === 'accepted' && (
            <div className="flex items-center gap-2 rounded-xl border border-blue-200 bg-blue-50 px-4 py-3 text-sm font-semibold text-blue-900 dark:border-blue-800 dark:bg-blue-900/20 dark:text-blue-200">
              <Package className="h-4 w-4" />
              {t('travelerMyRequests.deliveryInProgress')}
            </div>
          )}
          {canMarkAsDelivered && (
            <button
              onClick={() => handleMarkAsDelivered(request.id)}
              disabled={processingRequest === request.id}
              className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-green-600 text-white rounded-xl font-bold hover:bg-green-700 transition-all duration-200 shadow-sm hover:shadow-md disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Package className="h-5 w-5" />
              {t('travelerMyRequests.markAsDelivered')}
            </button>
          )}

          {canCancel && (
            <button
              onClick={() => handleCancelRequest(request.id)}
              disabled={processingRequest === request.id}
              className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-red-600 text-white rounded-xl font-bold hover:bg-red-700 transition-all duration-200 shadow-sm hover:shadow-md disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <XCircle className="h-5 w-5" />
              {t('travelerMyRequests.cancelRequest')}
            </button>
          )}

          <div className="flex gap-3">
            <button
              onClick={() => {
                handleStartChat(ownerInfo.id, ownerInfo.name);
              }}
              className="flex-1 flex items-center justify-center gap-2 px-4 py-3 bg-gray-100 dark:bg-slate-800 text-gray-900 dark:text-white rounded-xl font-bold hover:bg-gray-200 dark:hover:bg-slate-700 transition-all duration-200"
            >
              <MessageCircle className="h-5 w-5" />
              {t('travelerMyRequests.message')}
            </button>
            <button
              onClick={() => navigate('/profile/support')}
              className="flex-1 flex items-center justify-center gap-2 px-4 py-3 bg-gray-100 dark:bg-slate-800 text-gray-900 dark:text-white rounded-xl font-bold hover:bg-gray-200 dark:hover:bg-slate-700 transition-all duration-200"
            >
              <MessageSquare className="h-5 w-5" />
              {t('profile.supportTitle') || 'Support'}
            </button>
          </div>
          {(request.status === 'delivered' ||
            request.status === 'completed') && (
            <button
              onClick={() => {
                if (!reviewStatuses[request.id]) {
                  handleOpenReviewModal(request);
                }
              }}
              disabled={reviewStatuses[request.id]}
              className={`w-full flex items-center justify-center gap-2 px-4 py-3 rounded-xl font-bold transition-all duration-200 shadow-sm ${
                reviewStatuses[request.id]
                  ? 'bg-gray-200 dark:bg-slate-800 text-gray-600 dark:text-slate-400 cursor-not-allowed'
                  : 'bg-yellow-500 text-black hover:bg-yellow-400'
              }`}
            >
              <Star className="h-5 w-5" />
              {reviewStatuses[request.id]
                ? t('travelerMyRequests.reviewed')
                : t('travelerMyRequests.rate')}
            </button>
          )}
        </div>
      </div>
    );
  };

  const isLoading = initializing || loading;

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-slate-950">
        <MainNavbar />

        {/* Hero Section Skeleton */}
        <section className="relative bg-gradient-to-br from-yellow-400 via-yellow-500 to-yellow-600 dark:from-slate-800 dark:via-slate-900 dark:to-black overflow-hidden pt-28 pb-16 min-h-[500px]">
          <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8 relative z-10">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
              <div>
                <div className="h-16 bg-white/20 dark:bg-slate-700/50 rounded-xl mb-4 w-3/4 animate-pulse"></div>
                <div className="h-6 bg-white/20 dark:bg-slate-700/50 rounded-lg mb-2 w-full animate-pulse"></div>
                <div className="h-6 bg-white/20 dark:bg-slate-700/50 rounded-lg mb-2 w-5/6 animate-pulse"></div>
                <div className="h-6 bg-white/20 dark:bg-slate-700/50 rounded-lg w-4/6 animate-pulse"></div>
              </div>
              <div className="relative">
                <div className="w-full h-96 bg-white/20 dark:bg-slate-700/50 rounded-2xl animate-pulse"></div>
              </div>
            </div>
          </div>
        </section>

        <main className="mx-auto max-w-6xl px-4 pt-16 pb-16 sm:px-6 lg:px-8">
          {/* Cards Skeleton */}
          <div className="mb-8">
            <div className="h-8 bg-gray-200 dark:bg-slate-800 rounded-lg w-64 mb-2 animate-pulse"></div>
            <div className="h-4 bg-gray-200 dark:bg-slate-800 rounded-lg w-96 animate-pulse"></div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[...Array(6)].map((_, index) => (
              <div
                key={index}
                className="rounded-2xl bg-white dark:bg-slate-900 p-6 shadow-md border border-gray-200 dark:border-slate-800"
              >
                <div className="h-6 bg-gray-200 dark:bg-slate-800 rounded-lg w-3/4 mb-4 animate-pulse"></div>
                <div className="h-4 bg-gray-200 dark:bg-slate-800 rounded-lg w-full mb-2 animate-pulse"></div>
                <div className="h-4 bg-gray-200 dark:bg-slate-800 rounded-lg w-5/6 mb-4 animate-pulse"></div>
                <div className="h-20 bg-gray-200 dark:bg-slate-800 rounded-xl mb-4 animate-pulse"></div>
                <div className="flex gap-3 mb-4">
                  <div className="h-8 bg-gray-200 dark:bg-slate-800 rounded-lg flex-1 animate-pulse"></div>
                  <div className="h-8 bg-gray-200 dark:bg-slate-800 rounded-lg flex-1 animate-pulse"></div>
                </div>
                <div className="h-12 bg-gray-200 dark:bg-slate-800 rounded-xl animate-pulse"></div>
              </div>
            ))}
          </div>
        </main>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-slate-950">
        <MainNavbar />
        <main className="mx-auto max-w-6xl px-4 pt-28 pb-16 sm:px-6 lg:px-8">
          <div className="rounded-3xl bg-white dark:bg-slate-900 p-8 shadow-lg text-center border border-gray-200 dark:border-slate-800">
            <p className="text-gray-600 dark:text-slate-400 mb-4">
              {t('travelerMyRequests.pleaseLogInToView')}
            </p>
            <a
              href="/auth/login"
              className="inline-flex rounded-2xl bg-black dark:bg-yellow-500 px-5 py-3 text-sm font-semibold text-yellow-400 dark:text-black transition hover:bg-gray-900 dark:hover:bg-yellow-400"
            >
              {t('travelerMyRequests.goToLogin')}
            </a>
          </div>
        </main>
      </div>
    );
  }

  return (
    <>
      <div className="min-h-screen bg-gray-50 dark:bg-slate-950">
        <MainNavbar />

        {/* Hero Section */}
        <section className="relative bg-gradient-to-br from-yellow-400 via-yellow-500 to-yellow-600 dark:from-slate-800 dark:via-slate-900 dark:to-black overflow-hidden pt-28 pb-16 min-h-[500px]">
          <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8 relative z-10">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
              <div>
                <h1 className="text-5xl md:text-6xl font-black text-white mb-4 drop-shadow-lg">
                  {t('travelerMyRequests.myRequests')}
                </h1>
                <p className="text-xl text-yellow-50 mb-8 max-w-2xl">
                  {t('travelerMyRequests.viewAndManageRequests')}
                </p>
              </div>
              <div className="relative">
                <img
                  src={airportImage}
                  alt="Airport"
                  className="w-full h-auto object-contain"
                  style={{ maxHeight: '400px' }}
                />
              </div>
            </div>
          </div>

          {/* Decorative bottom gradient */}
          <div
            className="absolute bottom-0 left-0 w-full h-24 bg-gradient-to-t from-white/20 to-transparent"
            style={{ zIndex: 1 }}
          ></div>
        </section>

        <main className="mx-auto max-w-6xl px-4 pt-16 pb-16 sm:px-6 lg:px-8">
          {requests.length === 0 ? (
            <div className="rounded-3xl bg-white dark:bg-slate-900 p-12 shadow-lg text-center border border-gray-200 dark:border-slate-800">
              <Box className="h-16 w-16 text-gray-300 dark:text-slate-600 mx-auto mb-4" />
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
                {t('travelerMyRequests.noRequestsYet')}
              </h2>
              <p className="text-gray-600 dark:text-slate-400 mb-6">
                {t('travelerMyRequests.haventSentAnyRequests')}
              </p>
              <a
                href="/trips/sender"
                className="inline-flex rounded-2xl bg-black dark:bg-yellow-500 px-6 py-3 text-sm font-semibold text-yellow-400 dark:text-black transition hover:bg-gray-900 dark:hover:bg-yellow-400"
              >
                {t('travelerMyRequests.browseSenderListings')}
              </a>
            </div>
          ) : (
            <>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {(() => {
                  const sortedRequests = [...requests].sort((a, b) => {
                    const aTime = a.createdAt?.toDate
                      ? a.createdAt.toDate().getTime()
                      : a.createdAt
                      ? new Date(a.createdAt).getTime()
                      : 0;
                    const bTime = b.createdAt?.toDate
                      ? b.createdAt.toDate().getTime()
                      : b.createdAt
                      ? new Date(b.createdAt).getTime()
                      : 0;
                    return bTime - aTime;
                  });
                  const totalPages = Math.max(
                    1,
                    Math.ceil(sortedRequests.length / PAGE_SIZE),
                  );
                  const startIndex = (currentPage - 1) * PAGE_SIZE;
                  const endIndex = startIndex + PAGE_SIZE;
                  const paginatedRequests = sortedRequests.slice(
                    startIndex,
                    endIndex,
                  );

                  return (
                    <>
                      {paginatedRequests.map(request => (
                        <div key={request.id}>{renderRequestCard(request)}</div>
                      ))}
                      {totalPages > 1 && (
                        <div className="col-span-full mt-8 flex items-center justify-center gap-2">
                          <button
                            onClick={() =>
                              setCurrentPage(prev => Math.max(1, prev - 1))
                            }
                            disabled={currentPage === 1}
                            className="rounded-full border border-gray-300 px-4 py-2 text-sm font-semibold text-gray-900 transition hover:border-gray-500 disabled:opacity-50 dark:border-slate-700 dark:text-slate-100 dark:hover:border-slate-500"
                          >
                            {t('travelerMyRequests.prev')}
                          </button>
                          {[...Array(totalPages)].map((_, index) => (
                            <button
                              key={index + 1}
                              onClick={() => setCurrentPage(index + 1)}
                              className={`rounded-full px-4 py-2 text-sm font-semibold transition ${
                                currentPage === index + 1
                                  ? 'bg-black text-yellow-400 dark:bg-yellow-500 dark:text-black'
                                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200 dark:bg-slate-800 dark:text-slate-300 dark:hover:bg-slate-700'
                              }`}
                            >
                              {index + 1}
                            </button>
                          ))}
                          <button
                            onClick={() =>
                              setCurrentPage(prev =>
                                Math.min(totalPages, prev + 1),
                              )
                            }
                            disabled={currentPage === totalPages}
                            className="rounded-full border border-gray-300 px-4 py-2 text-sm font-semibold text-gray-900 transition hover:border-gray-500 disabled:opacity-50 dark:border-slate-700 dark:text-slate-100 dark:hover:border-slate-500"
                          >
                            {t('travelerMyRequests.next')}
                          </button>
                        </div>
                      )}
                    </>
                  );
                })()}
              </div>
            </>
          )}
        </main>
      </div>
      <ReviewModal
        visible={reviewModal.visible}
        reviewedUserId={reviewModal.reviewedUserId}
        reviewedUserName={reviewModal.reviewedUserName}
        requestId={reviewModal.requestId}
        userRole={reviewModal.role}
        onClose={() =>
          setReviewModal(prev => ({
            ...prev,
            visible: false,
          }))
        }
        onSubmitSuccess={() => {
          if (reviewModal.requestId) {
            handleReviewSuccess(reviewModal.requestId);
          }
        }}
      />
    </>
  );
}
